import {
  InterviewConfig,
  InterviewTopic,
  TopicArchetype,
  TopicType
} from '@/types/pulse';
import {
  Conversation,
  ConversationRewardLimited,
  ConversationWCodes
} from '@/types/pulsedb';
import * as yaml from 'js-yaml';
import Mustache from 'mustache';

export function createTopicGuid(): string {
  return crypto.randomUUID();
}

export function loadInterviewConfig(config: string): InterviewConfig | null {
  try {
    const data = yaml.load(config, {
      schema: yaml.JSON_SCHEMA
    }) as InterviewConfig;
    return data;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export function configToYaml(config: InterviewConfig) {
  return yaml.dump(config);
}

// The agent's default silence check-in (IDLE_PROMPT_SECONDS in
// pulseiq-live-kit's src/pulseiq/idle.py), shown as the topic field's starting
// value. The agent owns it: a topic at the default stores no override.
export const DEFAULT_IDLE_PROMPT_SECONDS = 15;
export const MIN_IDLE_PROMPT_SECONDS = 5;
export const MAX_IDLE_PROMPT_SECONDS = 300;

export function compileCompleteUrl(url: string, context: Record<string, any>) {
  return Mustache.render(url, context);
}

/**
 * The datetime to display for a conversation: when the interview started,
 * falling back to when the row was written for conversations that have no
 * start. Display only — anything measuring the interview itself reads
 * `started_at` and treats null as "nothing to measure".
 */
export function conversationStartedAt(conversation: {
  started_at?: string | null;
  created_at: string;
}): string {
  return conversation.started_at ?? conversation.created_at;
}

/** The reward claim embedded on a conversation, if the query asked for one. */
export function conversationReward(
  conversation: ConversationWCodes
): ConversationRewardLimited | null {
  const reward = conversation.conversation_rewards;
  if (!reward) return null;
  return Array.isArray(reward) ? (reward[0] ?? null) : reward;
}

export function conversationDuration(conversation: Conversation) {
  const seconds = conversationDurationSeconds(conversation);
  if (seconds === undefined) return;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const formattedSeconds =
    remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

  return `${minutes}:${formattedSeconds}`;
}

export function conversationDurationSeconds(conversation: Conversation) {
  if (!conversation.started_at || !conversation.ended_at) return;
  const startedAt = new Date(conversation.started_at);
  const endedAt = new Date(conversation.ended_at);
  const seconds = Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000);
  return seconds;
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function calculateAverageDuration(
  conversations: Conversation[]
): string {
  const durations = conversations
    .map((c) => conversationDurationSeconds(c))
    .filter((d): d is number => d !== undefined);

  if (durations.length === 0) return '—';

  const avgSeconds = Math.floor(
    durations.reduce((a, b) => a + b, 0) / durations.length
  );
  return formatDuration(avgSeconds);
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function topicTypeLabel(topic: InterviewTopic): string {
  if (topic.type === TopicType.VOICE && topic.archetype != null) {
    return capitalize(TopicArchetype[topic.archetype]);
  }
  if (topic.type === TopicType.QUESTION) return 'Question';
  if (topic.type === TopicType.STATEMENT) return 'Statement';
  return topic.type ? capitalize(TopicType[topic.type]) : 'Voice';
}

export function topicToString(topic: InterviewTopic): string {
  const lines: string[] = [`## ${topic.name}`];
  if (topic.guid) lines.push(`GUID: ${topic.guid}`);
  lines.push(`Type: ${topicTypeLabel(topic)}`);

  if (topic.type === TopicType.VOICE) {
    if (topic.goals?.length) {
      lines.push('Goals:');
      lines.push(...topic.goals.map((g) => `- ${g}`));
    }
    if (topic.examples?.length) {
      lines.push('Examples:');
      lines.push(...topic.examples.map((e) => `- ${e}`));
    }
  } else if (topic.type === TopicType.QUESTION) {
    lines.push(`Question: ${topic.question}`);
    if (topic.probeQuestions?.length) {
      lines.push('Probe questions:');
      lines.push(...topic.probeQuestions.map((q) => `- ${q}`));
    }
  } else if (topic.type === TopicType.STATEMENT) {
    lines.push(`Statement: ${topic.statement}`);
  } else if (topic.type === TopicType.SURVEY_TEXT) {
    if (topic.options?.length) {
      lines.push('Options:');
      lines.push(...topic.options.map((o) => `- ${o}`));
    }
  }

  if (topic.probeDepth) {
    lines.push(`Probe Depth: ${topic.probeDepth} questions`);
  }
  if (topic.context) {
    lines.push(`Context: ${topic.context}`);
  }
  if (topic.skipIf?.condition) {
    lines.push(`Skip if: ${topic.skipIf.condition}`);
  }

  return lines.join('\n');
}

export function interviewConfigToString(config: InterviewConfig): string {
  const lines: string[] = ['# Interview Discussion Guide'];
  if (config.context) lines.push(`Context: ${config.context}`);
  lines.push('');

  if (config.kickoff) {
    lines.push('## Kickoff');
    lines.push('Type: Statement');
    lines.push(`Statement: ${config.kickoff}`);
    lines.push('');
  }

  for (const topic of config.topics) {
    lines.push(topicToString(topic));
    lines.push('');
  }

  if (config.signoff) {
    lines.push('## Signoff');
    lines.push('Type: Statement');
    lines.push(`Statement: ${config.signoff}`);
  }

  return lines.join('\n').trim();
}

// --- Natural-language ("to_llm_prompt") rendering ---------------------------
//
// A richer, LLM-readable rendering of the discussion guide than the terse
// `interviewConfigToString` form above: it spells out what each topic type and interviewer
// archetype *means*, uses natural-language field labels, and (at the config
// level) leads with high-level interview metadata. The rendering is semantic,
// not byte-faithful — list values render as comma-joins — since no downstream
// contract depends on exact bytes (unlike serialize.ts). Used to feed the guide
// to LLMs (analyze study context, report generation).

const TOPIC_TYPE_DESCRIPTIONS: Record<TopicType, string> = {
  [TopicType.VOICE]:
    'LLM Driven Conversation, LLM asks questions based on the goals of what the interview creater wanted to get out of this section. These are just the overall goals. This section is to be enriched with a further more detailed guide',
  [TopicType.QUESTION]:
    'Ask inital pre-selected question, then chose from potential listed follow up questions to ask',
  [TopicType.STATEMENT]: 'Simply repeat statement verbatim',
  [TopicType.SURVEY_TEXT]: 'Text survey question with free-form text input',
  [TopicType.SURVEY_MULTI_CHOICE]:
    'Multi-Choice survey question with predefined options',
  [TopicType.SURVEY_BOOLEAN]: 'Boolean survey question with yes/no options',
  [TopicType.SURVEY_DROPDOWN]:
    'Dropdown survey question with selectable options',
  [TopicType.SYSTEM_PROMPT_OVERRIDE]:
    'LLM conversation with system prompt override for the topic. Admin use only. Do not create a guide for this section'
};

const ARCHETYPE_DESCRIPTIONS: Record<TopicArchetype, string> = {
  [TopicArchetype.INQUISITOR]:
    'Curious exploration, really dig with follow up question to find underlying answers',
  [TopicArchetype.PROCEDURALIST]:
    'Find facts, do not dive into follow questions',
  [TopicArchetype.CHALLENGER]:
    "Challenge assumptions and play devil's advocate to push past surface-level answers and uncover real motivations",
  [TopicArchetype.CONVERSATIONALIST]:
    'Conversationalist, tone is friendly, engaging, and approachable'
};

const SURVEY_TYPES = new Set([
  TopicType.SURVEY_TEXT,
  TopicType.SURVEY_MULTI_CHOICE,
  TopicType.SURVEY_BOOLEAN,
  TopicType.SURVEY_DROPDOWN
]);

function joinList(items: string[]): string {
  return items.join(', ');
}

/** A topic rendered as natural language for an LLM. */
export function topicToLlmPrompt(topic: InterviewTopic, index: number): string {
  const lines: string[] = [`Topic #${index + 1}`];
  lines.push(`name: ${topic.name}`);
  if (topic.guid) lines.push(`guid: ${topic.guid}`);
  const typeDescription = TOPIC_TYPE_DESCRIPTIONS[topic.type];
  if (typeDescription) lines.push(`topic type: ${typeDescription}`);

  if (topic.type === TopicType.VOICE) {
    if (topic.archetype && ARCHETYPE_DESCRIPTIONS[topic.archetype]) {
      lines.push(
        `interviewer style for topic: ${ARCHETYPE_DESCRIPTIONS[topic.archetype]}`
      );
    }
    if (topic.goals?.length) {
      lines.push(`goals of topic: ${joinList(topic.goals)}`);
    }
    if (topic.examples?.length) {
      lines.push(`examples of possible questions: ${joinList(topic.examples)}`);
    }
    if (topic.maxTime) {
      lines.push(`time topic should take: ${topic.maxTime} minutes`);
    }
  } else if (topic.type === TopicType.QUESTION) {
    if (topic.question) {
      lines.push(`inital question asked in this topic: ${topic.question}`);
    }
    if (topic.probeQuestions?.length) {
      lines.push(`follow up questions: ${joinList(topic.probeQuestions)}`);
    }
    if (topic.probeDepth) {
      lines.push(
        `maximum number of follow up questions asked in this topic: ${topic.probeDepth}`
      );
    }
  } else if (topic.type === TopicType.STATEMENT) {
    lines.push(
      `topic's only purpose is to restate this statement verbaitum: ${topic.statement ?? ''}`
    );
  } else if (topic.type === TopicType.SYSTEM_PROMPT_OVERRIDE) {
    lines.push(
      `system prompt override for this topic: """${topic.sysprompt ?? ''}"""`
    );
  } else if (SURVEY_TYPES.has(topic.type)) {
    lines.push(
      `topic's only purpose is to ask a survey question with the following options: ${joinList(topic.options ?? [])}`
    );
  }

  if (topic.context) {
    lines.push(`additional context for this topic: ${topic.context}`);
  }
  if (topic.skipIf?.condition) {
    lines.push(
      `this topic is skipped for participants where: ${topic.skipIf.condition}`
    );
  }
  return lines.join('\n');
}

/** Interview-row fields that lead the config's natural-language rendering. */
export interface GuideInterviewRow {
  name?: string | null;
  title?: string | null;
  caption?: string | null;
  time_estimate?: string | null;
}

/** The full discussion guide rendered as natural language for an LLM. */
export function configToLlmPrompt(
  config: { topics?: InterviewTopic[]; context?: string },
  interviewRow?: GuideInterviewRow
): string {
  const lines: string[] = [
    '# Interview Config as Natural Language',
    'High Level Interview Information'
  ];
  if (interviewRow?.name) lines.push(`internal name: ${interviewRow.name}`);
  if (interviewRow?.title) lines.push(`external title: ${interviewRow.title}`);
  if (interviewRow?.caption) {
    lines.push(`external caption: ${interviewRow.caption}`);
  }
  if (interviewRow?.time_estimate) {
    lines.push(
      `estimated time for the whole interview: ${interviewRow.time_estimate}`
    );
  }

  const sections = [lines.join('\n')];
  (config.topics ?? []).forEach((topic, index) => {
    sections.push(topicToLlmPrompt(topic, index));
  });
  if (config.context) {
    sections.push(
      `overall context applicable to all topics: ${config.context}`
    );
  }
  return sections.join('\n\n').trim();
}
